import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { getConsent, saveConsent } from "@/lib/consent";
import { applyConsent } from "@/lib/tracking";
import { CookiePreferences } from "./CookiePreferences";

const ConsentContext = createContext({ openSettings: () => {} });

const BTN = "h-11 flex-1 rounded-full px-3 sm:px-5 font-heading text-sm font-extrabold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40";

const Banner = ({ onAccept, onReject, onCustomize }) => (
  <div role="region" aria-label="Cookie consent" data-testid="cookie-banner" className="fixed inset-x-3 bottom-3 z-40 mx-auto max-w-3xl rounded-[1.5rem] bg-white p-4 shadow-[0_30px_80px_-25px_rgba(18,52,90,0.55)] ring-1 ring-navy/10 sm:inset-x-6 sm:bottom-6 sm:p-6 animate-in fade-in slide-in-from-bottom-6 duration-500">
    <div className="flex items-start gap-4">
      <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gold/30 text-navy sm:flex"><Cookie className="h-5 w-5" aria-hidden="true" /></span>
      <div>
        <p className="font-heading text-base sm:text-lg font-extrabold text-navy">Your cookie choices</p>
        <p className="mt-1.5 text-[13px] sm:text-sm leading-relaxed text-navy/80">
          We use necessary storage to remember this choice and, with your permission, Google Analytics, Meta Pixel and Google Ads to measure and improve our ads. Change this anytime in Cookie Settings. See our <Link to="/cookies" data-testid="cookie-banner-policy-link" className="font-semibold text-teal-dark underline underline-offset-2">Cookie Policy</Link>.
        </p>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap items-center gap-2.5">
      <button type="button" data-testid="cookie-accept-all" onClick={onAccept} className={`${BTN} bg-navy text-cream hover:bg-[#0c2644]`}>Accept All</button>
      <button type="button" data-testid="cookie-reject-nonessential" onClick={onReject} className={`${BTN} bg-navy text-cream hover:bg-[#0c2644]`}>Reject Nonessential</button>
      <button type="button" data-testid="cookie-customize" onClick={onCustomize} className={`${BTN} basis-full border-2 border-navy text-navy hover:bg-navy/5 sm:basis-0`}>Customize</button>
    </div>
  </div>
);

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [ready, setReady] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    const c = getConsent();
    setConsent(c);
    applyConsent(c);
    setReady(true);
  }, []);

  const save = useCallback((prefs) => {
    const saved = saveConsent(prefs);
    setConsent(saved);
    applyConsent(saved);
    setPrefsOpen(false);
  }, []);

  const openSettings = useCallback(() => setPrefsOpen(true), []);

  return (
    <ConsentContext.Provider value={{ openSettings }}>
      {children}
      {ready && !consent && !prefsOpen && (
        <Banner
          onAccept={() => save({ analytics: true, advertising: true })}
          onReject={() => save({ analytics: false, advertising: false })}
          onCustomize={openSettings}
        />
      )}
      <CookiePreferences open={prefsOpen} onOpenChange={setPrefsOpen} current={consent} onSave={save} />
    </ConsentContext.Provider>
  );
};

export const useConsent = () => useContext(ConsentContext);
